/*
 * Studio's provider and compute costs, as the SuperAdmin cost report (GET
 * /api/studio-admin/costs) returns them. Amounts are what Tapybl pays its providers in USD,
 * never what a workspace is charged: only SuperAdmins see them, never authors.
 *
 * The report is read through `normalizeStudioCostReport`, which tolerates fields an older or
 * newer API leaves out or names differently. A cost the server could not price is `null`,
 * never 0: unknown costs are counted (`unknownCostReceipts`), not added in.
 *
 * What the API sends (StudioCostReport.cs): totals and each job carry `aiUsd`, `computeUsd`,
 * `receipts` and `unknownCostReceipts`, with `aiUsd` 0 even when every receipt's cost is
 * unknown. A stage, reason, model, render or microreel line carries `usd` (its known AI cost
 * only, left out when none is known), `computeUsd` beside it, `calls` and `unknownCostCalls`.
 * A job names its workspace by `organizationId` only.
 */

/** The report's filters; `from` and `to` are ISO instants, `to` exclusive. */
export interface StudioCostQuery {
  from?: string;
  to?: string;
  organizationId?: string;
  jobId?: string;
  /** How many jobs to list; the API allows up to 200. */
  take?: number;
}

export interface StudioCostTotals {
  aiUsd: number | null;
  computeUsd: number | null;
  /** What the render fleet cost in the period, when the API is configured with its rate. */
  fleetUsd: number | null;
  /** Receipts whose provider cost is unknown, so not in any amount. */
  unknownCostReceipts: number;
  /** Receipts priced at the claim price because the call's outcome was never saved. */
  claimPricedReceipts: number;
  /** Cost recorded before attribution existed, or by an older worker: no stage or reason. */
  unattributedUsd: number | null;
}

/** A total for one stage, reason, model, render or microreel. */
export interface StudioCostBucket {
  /** The stage, reason, model or microreel id; '' when the server recorded none. */
  key: string;
  /** A name to show for it, when the server sends one (e.g. a microreel's title). */
  label?: string;
  /** Its known AI and compute cost; null when neither is known. */
  usd: number | null;
  /** Its known AI cost; null when none is (every call's cost unknown, or no calls). */
  aiUsd: number | null;
  computeUsd: number | null;
  calls?: number;
  tokens?: number;
  characters?: number;
  images?: number;
  vcpuSeconds?: number;
  /** Calls whose cost is unknown, so not in `usd` or `aiUsd`. */
  unknownCostReceipts?: number;
}

/** One render of a job, numbered in the order they ran. */
export interface StudioCostRender extends StudioCostBucket {
  number: number;
  startedAt?: string;
}

export interface StudioCostJob {
  jobId: string;
  title: string;
  organizationId?: string;
  /** The workspace's name; the API sends only its id today. */
  organizationName?: string;
  renderCount: number;
  /** How many microreels the job has; the per-reel totals' count when the server says none. */
  reelCount: number;
  /**
   * Whether the server sent the job's microreel count. When it did not, `reelCount` is only the
   * microreels that had a cost in the period, so it is no divisor for a cost per microreel.
   */
  reelCountReported: boolean;
  aiUsd: number | null;
  computeUsd: number | null;
  unknownCostReceipts: number;
  chargedCredits?: number;
  heldCredits?: number;
  renders: StudioCostRender[];
  reels: StudioCostBucket[];
  byStage: StudioCostBucket[];
  byReason: StudioCostBucket[];
}

export interface StudioCostReport {
  from?: string;
  to?: string;
  totals: StudioCostTotals;
  byStage: StudioCostBucket[];
  byReason: StudioCostBucket[];
  byModel: StudioCostBucket[];
  jobs: StudioCostJob[];
}

type Raw = Record<string, unknown>;

const isObject = (value: unknown): value is Raw =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/** A finite number, or a numeric string; undefined otherwise. */
function num(value: unknown): number | undefined {
  if (typeof value === 'number')
    return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** The first of `keys` that holds a number. */
function pick(raw: Raw, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = num(raw[key]);
    if (value !== undefined) return value;
  }
  return undefined;
}

function text(raw: Raw, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
  }
  return undefined;
}

const list = (raw: Raw, ...keys: string[]): unknown[] => {
  for (const key of keys)
    if (Array.isArray(raw[key])) return raw[key] as unknown[];
  return [];
};

/** Adds amounts that may be unknown: null only when every one is. */
export function addUsd(
  ...amounts: (number | null | undefined)[]
): number | null {
  const known = amounts.filter((a): a is number => typeof a === 'number');
  return known.length ? known.reduce((sum, a) => sum + a, 0) : null;
}

/**
 * An AI amount from totals or a job, where the API sends 0 when it knows none of the costs:
 * null when it has receipts and every one's cost is unknown.
 */
function knownAiUsd(
  usd: number | undefined,
  receipts: number | undefined,
  unknown: number,
): number | null {
  if (usd === undefined) return null;
  const allUnknown = !!receipts && receipts > 0 && unknown >= receipts;
  return usd === 0 && allUnknown ? null : usd;
}

function bucket(
  value: unknown,
  ...keyNames: string[]
): StudioCostBucket | null {
  if (!isObject(value)) return null;
  // A line's `usd` is its known AI cost only; compute is `computeUsd` beside it.
  const aiUsd = pick(value, 'aiUsd', 'usd', 'costUsd') ?? null;
  const computeUsd = pick(value, 'computeUsd') ?? null;
  const input = pick(value, 'inputTokens');
  const output = pick(value, 'outputTokens');
  const tokens =
    pick(value, 'tokens') ??
    (input === undefined && output === undefined
      ? undefined
      : (input ?? 0) + (output ?? 0));
  return {
    key: text(value, ...keyNames, 'key', 'name') ?? '',
    label: text(value, 'title', 'label'),
    usd: pick(value, 'totalUsd') ?? addUsd(aiUsd, computeUsd),
    aiUsd,
    computeUsd,
    calls: pick(value, 'calls', 'count', 'receipts'),
    tokens,
    characters: pick(value, 'characters'),
    images: pick(value, 'images'),
    vcpuSeconds: pick(value, 'vcpuSeconds', 'vCpuSeconds'),
    unknownCostReceipts: pick(
      value,
      'unknownCostCalls',
      'unknownCostReceipts',
      'unknownCosts',
    ),
  };
}

const buckets = (values: unknown[], ...keyNames: string[]) =>
  values
    .map(value => bucket(value, ...keyNames))
    .filter((b): b is StudioCostBucket => !!b);

function renders(values: unknown[]): StudioCostRender[] {
  return values
    .map((value, index): StudioCostRender | null => {
      const b = bucket(value, 'operationId', 'stageOperationId', 'id');
      if (!b || !isObject(value)) return null;
      return {
        ...b,
        number: pick(value, 'number', 'renderNumber', 'index') ?? index + 1,
        startedAt: text(value, 'startedAt', 'createdAt', 'recordedAt'),
      };
    })
    .filter((r): r is StudioCostRender => !!r);
}

function job(value: unknown): StudioCostJob | null {
  if (!isObject(value)) return null;
  const jobId = text(value, 'jobId', 'id');
  if (!jobId) return null;
  const renderList = Array.isArray(value.renders)
    ? renders(value.renders as unknown[])
    : renders(list(value, 'byRender'));
  const reels = buckets(list(value, 'reels', 'byReel'), 'reelId');
  const unknownCostReceipts = pick(value, 'unknownCostReceipts') ?? 0;
  const aiUsd = knownAiUsd(
    pick(value, 'aiUsd'),
    pick(value, 'receipts'),
    unknownCostReceipts,
  );
  const computeUsd = pick(value, 'computeUsd') ?? null;
  const reportedReels =
    pick(value, 'reelCount', 'microreelCount') ?? num(value.reels);
  return {
    jobId,
    title: text(value, 'title', 'jobTitle') || '',
    organizationId: text(value, 'organizationId'),
    organizationName: text(value, 'organizationName', 'workspaceName'),
    renderCount:
      pick(value, 'renderCount') ?? num(value.renders) ?? renderList.length,
    reelCount: reportedReels ?? reels.filter(reel => reel.key).length,
    reelCountReported: reportedReels !== undefined,
    aiUsd,
    computeUsd,
    unknownCostReceipts,
    chargedCredits: pick(value, 'chargedCredits'),
    heldCredits: pick(value, 'heldCredits'),
    renders: renderList,
    reels,
    byStage: buckets(list(value, 'byStage', 'stages'), 'stage'),
    byReason: buckets(list(value, 'byReason', 'reasons'), 'reason'),
  };
}

/** The report as the page shows it, whatever fields the API sent; null for no report at all. */
export function normalizeStudioCostReport(
  raw: unknown,
): StudioCostReport | null {
  if (!isObject(raw)) return null;
  const totals = isObject(raw.totals) ? raw.totals : {};
  const unknownCostReceipts = pick(totals, 'unknownCostReceipts') ?? 0;
  return {
    from: text(raw, 'from'),
    to: text(raw, 'to'),
    totals: {
      aiUsd: knownAiUsd(
        pick(totals, 'aiUsd'),
        pick(totals, 'receipts'),
        unknownCostReceipts,
      ),
      computeUsd: pick(totals, 'computeUsd') ?? null,
      fleetUsd: pick(totals, 'fleetUsd') ?? null,
      unknownCostReceipts,
      claimPricedReceipts: pick(totals, 'claimPricedReceipts') ?? 0,
      unattributedUsd: pick(totals, 'unattributedUsd') ?? null,
    },
    byStage: buckets(list(raw, 'byStage', 'stages'), 'stage'),
    byReason: buckets(list(raw, 'byReason', 'reasons'), 'reason'),
    byModel: buckets(list(raw, 'byModel', 'models'), 'model'),
    jobs: list(raw, 'jobs')
      .map(job)
      .filter((j): j is StudioCostJob => !!j),
  };
}

/**
 * A job's known AI and compute cost per microreel; null when it has no microreels, no known
 * cost, or the server did not say how many microreels it has (the microreels that merely had a
 * cost in the period would spread its planning, finalize and publish costs over too few).
 * Costs still unknown (`job.unknownCostReceipts`) are not in it, so whatever shows it
 * says how many there are beside it.
 */
export function costPerReel(job: StudioCostJob): number | null {
  const total = addUsd(job.aiUsd, job.computeUsd);
  return total === null || !job.reelCountReported || job.reelCount <= 0
    ? null
    : total / job.reelCount;
}
